/* @flow */

import React, { Component } from 'react';
import L from 'leaflet';
import { Map } from 'react-leaflet';
import { Marker } from 'react-leaflet';
import { TileLayer } from 'react-leaflet';
import { ZoomControl } from 'react-leaflet';
import Globals from '../../constants/Globals';
import MapGestureHandling from '../MapGestureHandling/MapGestureHandling';
import MarketGeoMappings from './MarketGeoMappings';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import WorkbenchIcons from '../WorkbenchIcon/WorkbenchIcons';
import type { $Coordinates } from './Coordinates';
import type { $MarketGeoMapping } from './MarketGeoMappings';
import type { $MapGestureHandlingOptions } from '../MapGestureHandling/MapGestureHandlingOptions';
import type { $MapProps } from './Map';
import { getHereMapsTilesURL } from './HereMaps/HereMapsTilesAPI';
import { mapPropsAreDifferent } from '../../utils/MapUtils';

type $Props = {
  market: string,
  searchTerm?: string,
  enableMarketGeoMapping?: boolean,
  initialZoomConfig?: number,
  initialPositionConfig?: $Coordinates,
  rtl?: boolean,
  map: $MapProps,
  mapGestureHandlingEnabled?: boolean,
  mapGestureHandlingOptions?: $MapGestureHandlingOptions
};

export default class MapProvider extends Component<$Props> {

  lefletMap = null;

  constructor(props: $Props) {
    super(props);

    if (this.props.mapGestureHandlingEnabled) {
      L.Map.addInitHook('addHandler', 'mapGestureHandling', MapGestureHandling);
    }
  }

  shouldComponentUpdate(nextProps: $Props) {
    return mapPropsAreDifferent(this.props.map, nextProps.map);
  }

  getMarketMapping = (bounds: L.LatLngBounds, market: string, mappingEnabled: ?boolean): $MarketGeoMapping => {
    let geoMarketMapping = {
      marketCode: [market],
      center: bounds.getCenter(),
      zoom: 0,
      bounds
    };

    if (mappingEnabled) {
      for (const mapping of Object.keys(MarketGeoMappings)) {
        if (MarketGeoMappings.hasOwnProperty(mapping) && MarketGeoMappings[mapping].marketCode &&
          MarketGeoMappings[mapping].marketCode.includes(market)) {
          geoMarketMapping = MarketGeoMappings[mapping]
        }
      }
    }

    // If we have one of them configured, remove precalculated bounds
    // If we have both, use both
    // If we only have the zoom configured, get center from the calculated bounds
    // If we only have the central position, we have no way to calculate the zoom level; calculate new bounds

    if (this.props.initialZoomConfig || this.props.initialPositionConfig) {
      geoMarketMapping.bounds = undefined

      if (this.props.initialZoomConfig && this.props.initialPositionConfig) {
        geoMarketMapping.center = this.props.initialPositionConfig
        geoMarketMapping.zoom = ((this.props.initialZoomConfig: any): number)
      } else if (this.props.initialZoomConfig) {
        geoMarketMapping.center = bounds.getCenter()
        geoMarketMapping.zoom = ((this.props.initialZoomConfig: any): number)
      } else if (this.props.initialPositionConfig) {

        //Calculate the coordinates diff between the bounds' center and our config center
        const { lat, lng } = this.props.initialPositionConfig
        const centerLatDiff = lat - bounds.getCenter().lat
        const centerLngDiff = lng - bounds.getCenter().lng

        // Get the NorthWest and SouthEast corner
        const northWestCorner = bounds.getNorthWest()
        const southEastCorner = bounds.getSouthEast()

        // Calculate the new corners that makeup the center point
        const newNorthWestCorner = new L.LatLng(
          northWestCorner.lat + centerLatDiff,
          northWestCorner.lng + centerLngDiff
        );
        const newSouthEastCorner = new L.LatLng(
          southEastCorner.lat + centerLatDiff,
          southEastCorner.lng + centerLngDiff
        );
        const newBounds = new L.LatLngBounds(
          newNorthWestCorner,
          newSouthEastCorner
        );

        // Expected: newBounds.getCenter() === initialPositionConfig

        geoMarketMapping.bounds = newBounds
        geoMarketMapping.center = newBounds.getCenter()
      }
    }

    return geoMarketMapping
  };

  handleDealerClick = (outletId: ?string) => {
    if (outletId && this.props.map.onDealerClick) this.props.map.onDealerClick(outletId);
  };

  render() {
    const { map, market } = this.props;
    const hereTileURL = getHereMapsTilesURL(
      market,
      map.hereMapsLanguage, map.hereMapsTheme
    );

    let boundsList: Array<*> = [];
    if (this.props.map.markers && Array.isArray(this.props.map.markers) && this.props.map.markers.length > 0) {
      boundsList.push(...this.props.map.markers);
    }
    if (this.props.map.centralPoint) boundsList.push(this.props.map.centralPoint);
    const bounds = new L.LatLngBounds(boundsList);

    const mapProps: $MarketGeoMapping = this.getMarketMapping(
      bounds, this.props.market, this.props.searchTerm ? false : this.props.enableMarketGeoMapping);

    const dealerIcon = L.divIcon({
      html: WorkbenchIcons.ICO_DEALER_MARKER,
      className: 'dl-dealer-marker',
      iconSize: [34, 34],
      iconAnchor: [17, 34]
    });

    const createClusterCustomIcon = (cluster) => {
      return L.divIcon({
        html: `${cluster.getChildCount()}`,
        className: 'dl-marker-cluster',
        iconSize: [34, 34]
      });
    }

    const centralPointIcon = L.divIcon({
      html: WorkbenchIcons.ICO_MAP_CENTRAL_POINT,
      className: 'dl-marker-central-point',
      iconSize: [100, 100],
      iconAnchor: [50, 50]
    });

    const { mapPadding } = this.props.map;
    const boundsPadding: Object = {
      topLeft: L.point(
        mapPadding && mapPadding.left || Globals.MAPS_PADDING_DEFAULT,
        mapPadding && mapPadding.top || Globals.MAPS_PADDING_DEFAULT),
      bottomRight: L.point(
        mapPadding && mapPadding.right || Globals.MAPS_PADDING_DEFAULT,
        mapPadding && mapPadding.bottom || Globals.MAPS_PADDING_DEFAULT)
    };

    const zoomControlPosition = this.props.rtl ? 'topleft' : 'topright';

    return (
      <div className="dl-leaflet">
        <Map
          ref={mapRef => this.lefletMap = mapRef && mapRef.leafletElement}
          attributionControl={false}
          bounds={mapProps.bounds}
          boundsOptions={{
            paddingTopLeft: boundsPadding.topLeft,
            paddingBottomRight: boundsPadding.bottomRight
          }}
          center={mapProps.center}
          mapGestureHandling={this.props.mapGestureHandlingEnabled}
          mapGestureHandlingOptions={this.props.mapGestureHandlingOptions}
          scrollWheelZoom={this.props.map.scrollZoom}
          trackResize={true} /* DL-1438 */
          zoom={mapProps.zoom}
          zoomSnap={1.0}
          zoomDelta={1.0}
          minZoom={4}
          /* DL-1562 */
          /* Enabled animations lead to permanent bugs in zoom levels during map rerender in mobile safari */
          zoomAnimation={false}
          zoomControl={false}>
          <TileLayer url={hereTileURL} />
          {this.props.map.centralPoint &&
            <Marker position={this.props.map.centralPoint} icon={centralPointIcon} zIndexOffset={-5000} />
          }
          <MarkerClusterGroup
            showCoverageOnHover={false}
            iconCreateFunction={createClusterCustomIcon}
            maxClusterRadius={50}>
            {this.props.map.markers && this.props.map.markers.map((marker: $Coordinates, idx: number) => (
              <Marker key={idx} position={marker} icon={dealerIcon} onClick={() => {
                this.handleDealerClick(marker.outletId);
              }} />
            ))}
          </MarkerClusterGroup>
          <ZoomControl position={zoomControlPosition} zoomInTitle="" zoomOutTitle="" />
        </Map>
      </div>
    );
  }
}
